import React from 'react'
import { FormattedMessage, IntlShape } from 'react-intl'
import { css } from '@emotion/css'
import {
  Dialog,
  DialogBody,
  DialogFooter
} from '../../legacy-design-system/modal'
import { Row, Box, FormValidator } from '../../legacy-design-system/form'
import { Textarea } from '../../legacy-design-system/form-text'
import { SingleSelectComboboxObject } from '../../legacy-design-system/combobox'
import { I18nConsumer } from '../i18n-provider'
import { Label } from '../../legacy-design-system/form-label'
import {
  Status,
  Substatus,
  getDefaultSubstatusRegistry,
  SubstatusRegistry
} from './status'
import { RequiredStatusToggle } from './required-status-toggle'
import { SubstatusAliasQuery } from './substatus-alias-query'
import { Button } from '@contrast-security-inc/design-system-buttons'

export const getItemToString = (
  substatus: Substatus,
  intl: IntlShape,
  substatusAlias: Substatus | null
): string => {
  if (
    substatusAlias !== null &&
    substatus.name === substatusAlias.name &&
    substatusAlias.alias
  ) {
    return substatusAlias.alias
  }
  return intl.formatMessage({ id: substatus.i18nID })
}

// use substatus alias instead of 'Other' if one is set
export const getSubstatusItems = (
  intl: IntlShape,
  substatusAlias: Substatus | null,
  substatusRegistry: SubstatusRegistry
): Substatus[] => {
  let substatuses = substatusRegistry.all

  if (substatusAlias !== null) {
    substatuses = substatuses.filter(
      (substatus) => substatus.name !== substatusAlias.name
    )

    substatuses.push(substatusAlias)
    return sortSubstatuses(substatusAlias.name, substatuses, intl)
  }
  return sortSubstatuses('', substatuses, intl)
}

// sorts substatuses alphabetically
const sortSubstatuses = (
  substatusWithoutI18nId: string,
  substatuses: Substatus[],
  intl: IntlShape
): Substatus[] => {
  substatuses.sort((substatus1, substatus2) => {
    let label1 =
      substatus1.name === substatusWithoutI18nId
        ? substatus1.alias
        : intl.formatMessage({ id: substatus1.i18nID })
    let label2 =
      substatus2.name === substatusWithoutI18nId
        ? substatus1.alias
        : intl.formatMessage({ id: substatus2.i18nID })

    label1 = label1 ? label1.toLowerCase() : label1
    label2 = label2 ? label2.toLowerCase() : label2

    if (label1 && label2) {
      if (label1 < label2) {
        return -1
      } else if (label1 > label2) {
        return 1
      }
    }
    return 0
  })

  return substatuses
}

interface FormValue {
  readonly substatus?: Substatus
  readonly reasoning?: string
}

interface Filters {
  readonly filterText: string
  readonly quickFilter: string
  readonly aggregations: Record<string, string[]>
}

interface Props {
  readonly status: Status
  readonly isOpen: boolean
  readonly onCancel: () => void
  readonly onConfirm: (value: FormValue) => void
  readonly organizationID: string
  readonly traceIDs: string[]
  readonly filters?: Filters
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly isStatusApprovalEnabled: boolean
}

interface State {
  readonly formValue: FormValue
}
export class UpdateStatusDialog extends React.Component<Props, State> {
  constructor(props) {
    super(props)

    this.state = {
      formValue: {
        substatus: undefined,
        reasoning: ''
      }
    }
  }

  public render() {
    const {
      status,
      isOpen,
      onCancel,
      onConfirm,
      organizationID,
      traceIDs,
      filters,
      additionalFilters,
      isStatusApprovalEnabled
    } = this.props
    const { formValue } = this.state

    return (
      <RequiredStatusToggle
        organizationID={organizationID}
        traceIDs={traceIDs}
        statusName={status.name}
        filters={filters}
        additionalFilters={additionalFilters}
        isStatusApprovalEnabled={isStatusApprovalEnabled}
      >
        {(required) => {
          const requiresReason = status.requiresReason()
          const substatusRegistry = required
            ? getDefaultSubstatusRegistry()
            : status.substatusRegistry
          const requiresJustification =
            required || status.requiresJustification(formValue.substatus)

          return (
            <I18nConsumer>
              {({ intl }) => {
                return (
                  <FormValidator value={this.state.formValue}>
                    {({ getFormFieldProps, formIsValid }) => {
                      return (
                        <FormattedMessage id={status.i18nID}>
                          {(statusLabel) => {
                            return (
                              <FormattedMessage
                                id="variable_vulnerability_markAs"
                                values={{ status: statusLabel }}
                              >
                                {(title) => {
                                  return (
                                    <Dialog
                                      isOpen={isOpen}
                                      onCancel={onCancel}
                                      renderTitle={() => title}
                                      size="small"
                                    >
                                      <form noValidate>
                                        <DialogBody
                                          className={css`
                                            padding: 5px 20px;
                                          `}
                                        >
                                          {requiresReason && (
                                            <Row>
                                              <Box>
                                                <SubstatusAliasQuery
                                                  organizationUuid={
                                                    organizationID
                                                  }
                                                  substatus={
                                                    Substatus.OTHER.name
                                                  }
                                                >
                                                  {({
                                                    substatusAlias = null
                                                  }) => {
                                                    return (
                                                      <SingleSelectComboboxObject<Substatus>
                                                        renderLabel={(
                                                          label,
                                                          id
                                                        ) => {
                                                          return (
                                                            <Label
                                                              htmlFor={id}
                                                              required={
                                                                requiresReason
                                                              }
                                                            >
                                                              {label}
                                                            </Label>
                                                          )
                                                        }}
                                                        label={intl.formatMessage(
                                                          {
                                                            id: 'title_vulnerabilityReason'
                                                          }
                                                        )}
                                                        id="updateStatus_reason"
                                                        items={[
                                                          {
                                                            items:
                                                              getSubstatusItems(
                                                                intl,
                                                                substatusAlias,
                                                                substatusRegistry
                                                              )
                                                          }
                                                        ]}
                                                        itemToID={(s) => s.name}
                                                        itemToString={(s) =>
                                                          getItemToString(
                                                            s,
                                                            intl,
                                                            substatusAlias
                                                          )
                                                        }
                                                        value={
                                                          formValue.substatus
                                                        }
                                                        onChange={
                                                          this.onSubstatusChange
                                                        }
                                                        placeholder={intl.formatMessage(
                                                          {
                                                            id: 'action_select'
                                                          }
                                                        )}
                                                      />
                                                    )
                                                  }}
                                                </SubstatusAliasQuery>
                                              </Box>
                                            </Row>
                                          )}
                                          <Row>
                                            <Box>
                                              <Textarea
                                                expand
                                                maxLength={65535}
                                                rows={8}
                                                required={requiresJustification}
                                                id="updateStatus_justification"
                                                label={
                                                  requiresReason
                                                    ? intl.formatMessage({
                                                        id: 'title_vulnerabilityJustification'
                                                      })
                                                    : undefined
                                                }
                                                placeholder={intl.formatMessage(
                                                  {
                                                    id: requiresJustification
                                                      ? 'placeholder_explanationRequired'
                                                      : 'placeholder_explanationOptional'
                                                  }
                                                )}
                                                value={formValue.reasoning}
                                                onChange={
                                                  this.onReasoningChange
                                                }
                                                {...getFormFieldProps<HTMLTextAreaElement>(
                                                  {
                                                    id: 'justification',
                                                    validator: ({
                                                      validity
                                                    }) => {
                                                      return (
                                                        validity.valid ||
                                                        !requiresJustification
                                                      )
                                                    }
                                                  }
                                                )}
                                              />
                                            </Box>
                                          </Row>
                                        </DialogBody>
                                        <DialogFooter>
                                          <FormattedMessage id="action_cancel">
                                            {(text) => {
                                              return (
                                                <Button
                                                  variant="secondary"
                                                  onClick={onCancel}
                                                >
                                                  {text}
                                                </Button>
                                              )
                                            }}
                                          </FormattedMessage>
                                          <FormattedMessage id="action_markVulnerabilityStatus_markStatus">
                                            {(text) => {
                                              return (
                                                <Button
                                                  data-e2e="mark-status-button"
                                                  className={css`
                                                    margin-left: 8px;
                                                  `}
                                                  autoFocus
                                                  onClick={() => {
                                                    onCancel()
                                                    onConfirm({
                                                      ...formValue,
                                                      reasoning:
                                                        formValue.reasoning ||
                                                        undefined
                                                    })
                                                  }}
                                                  isDisabled={
                                                    !formIsValid ||
                                                    (requiresReason &&
                                                      !formValue.substatus) ||
                                                    (requiresJustification &&
                                                      !formValue.reasoning)
                                                  }
                                                >
                                                  {text}
                                                </Button>
                                              )
                                            }}
                                          </FormattedMessage>
                                        </DialogFooter>
                                      </form>
                                    </Dialog>
                                  )
                                }}
                              </FormattedMessage>
                            )
                          }}
                        </FormattedMessage>
                      )
                    }}
                  </FormValidator>
                )
              }}
            </I18nConsumer>
          )
        }}
      </RequiredStatusToggle>
    )
  }

  private onSubstatusChange = (substatus: Substatus) => {
    this.setState(({ formValue }) => ({
      formValue: {
        ...formValue,
        substatus
      }
    }))
  }

  private onReasoningChange = (reasoning: string) => {
    this.setState(({ formValue }) => ({
      formValue: {
        ...formValue,
        reasoning
      }
    }))
  }
}
